// TaxSearchForm.js
import React, { useState } from 'react';
import { useNavigate, useParams, useOutletContext } from 'react-router-dom';
import LoadingSpinner from './LoadingSpinner';
import './TaxSearchForm.css';
import DatePicker from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';

// 1. Utility functions
function getYearMinusFiveMonths() {
  const date = new Date();
  date.setMonth(date.getMonth() - 5); // Move date back by 5 months
  return date.getFullYear().toString(); // e.g. "2024"
}

function getLastTwoDigits(yearString) {
  if (typeof yearString !== 'string') {
    yearString = yearString.toString();
  }
  return yearString.slice(-2); // e.g. "2024" -> "24"
}

// Utility function: formatting of Geo Code
function formatGeoCode(value) {
  // Remove non-digits
  const cleaned = value.replace(/[^a-zA-Z0-9]/g, '').slice(0, 15);

  const parts = [
    cleaned.slice(0, 4),
    cleaned.slice(4, 6),
    cleaned.slice(6, 7),
    cleaned.slice(7, 9),
    cleaned.slice(9, 11),
    cleaned.slice(11, 15)
  ];

  return parts.filter(Boolean).join('-');
}

function startOfTodayLocal() {
  const d = new Date();
  d.setHours(0, 0, 0, 0);
  return d;
}

function formatMMDDYYYY(date) {
  const mm = String(date.getMonth() + 1).padStart(2, '0');
  const dd = String(date.getDate()).padStart(2, '0');
  const yyyy = date.getFullYear();
  return `${mm}/${dd}/${yyyy}`;
}

// Trim leading/trailing whitespace from every string field (e.g. Tax ID, Tax Year,
// # of Years of History) so stray spaces don't fail validation or the search.
function trimFormData(data) {
  return Object.fromEntries(
    Object.entries(data).map(([key, value]) =>
      [key, typeof value === 'string' ? value.trim() : value]
    )
  );
}

const TaxSearchForm = () => {
  const { customerId } = useParams();
  const navigate = useNavigate();
  const { dataClient } = useOutletContext();

  // 2. useState with 4-digit year default
  const [formData, setFormData] = useState({
    taxID: '',
    firstName: '',
    lastName: '',
    mailingAddress: '',
    legalDescription: '',
    geoCode: '',
    payoffDate: startOfTodayLocal(),
    taxyear: getYearMinusFiveMonths(),  // Show 4-digit year in the UI
    yearshistory: '10',
    customerId: customerId,
  });
  
const isTaxDisabled =
  dataClient?.disable_list
    ?.split(",")
    .map(item => item.trim().toLowerCase())
    .includes("tax");

if (!dataClient) {
  return <LoadingSpinner />;
}

if (isTaxDisabled) {
  return <div>{dataClient.disable_message}</div>;
}

  // Handle input changes & Process Geo Code
  const handleChange = (e) => {
  const { name, value } = e.target;

    if (name === 'geoCode') {
      setFormData((prev) => ({
        ...prev,
        geoCode: formatGeoCode(value),
      }));
      return;
    }

    setFormData((prevData) => ({
      ...prevData,
      [name]: value,
    }));
  };

  // 3.5 handle field validation
  const validateForm = (formData) => {
    const errors = [];
  
    const defaultTaxYear = getYearMinusFiveMonths();
    const payoffDateIsDefault = formData.payoffDate && new Date(formData.payoffDate).setHours(0,0,0,0) === startOfTodayLocal().getTime();
  
    const isOnlyDefaults =
      formData.taxyear === defaultTaxYear &&
      formData.yearshistory === '10' &&
      formData.taxID.trim() === '' &&
      formData.firstName.trim() === '' &&
      formData.lastName.trim() === '' &&
      formData.mailingAddress.trim() === '' &&
      formData.legalDescription.trim() === '' &&
      formData.geoCode.trim() === '' &&
      payoffDateIsDefault;
  
    const notEnoughFilters =
      formData.taxID.trim() === '' &&
      formData.firstName.trim() === '' &&
      formData.lastName.trim() === '' &&
      formData.mailingAddress.trim() === '' &&
      formData.legalDescription.trim() === '' &&
      formData.geoCode.trim() === '';
  
    // Make these mutually exclusive
    if (isOnlyDefaults) {
      errors.push("Please provide at least one filter beyond Tax Year and Years of History.");
    } else if (notEnoughFilters) {
      errors.push("Please provide more search filters to display results.");
    }
  
    // --- Continue with other field validations ---
  
    if (formData.taxID && !/^[a-zA-Z0-9\-]+$/.test(formData.taxID)) {
      errors.push("Tax ID must be alphanumeric.");
    }
  
    if (formData.firstName && !/^[a-zA-Z\s]+$/.test(formData.firstName)) {
      errors.push("First Name can only contain letters and spaces.");
    }
  
    if (formData.lastName && !/^[a-zA-Z\s]+$/.test(formData.lastName)) {
      errors.push("Last Name can only contain letters and spaces.");
    }
  
    if (formData.geoCode && !/^[a-zA-Z0-9\-]+$/.test(formData.geoCode)) {
      errors.push("Geo Code must be alphanumeric.");
    }
  
    if (formData.payoffDate) {
      const picked = new Date(formData.payoffDate);
      picked.setHours(0, 0, 0, 0);

      const today = startOfTodayLocal();

      if (picked < today) {
        errors.push("Payoff Date cannot be earlier than today.");
      }
    }
  
    if (!/^\d{4}$/.test(formData.taxyear)) {
      const currentYear = new Date().getFullYear();
      errors.push(`Tax Year must be a 4-digit year (e.g., ${currentYear}).`);
    }
  
    const years = parseInt(formData.yearshistory, 10);
    if (isNaN(years) || years < 1 || years > 20) {
      errors.push("Years of History must be a number between 1 and 20.");
    }
  
    return errors;
  };
  
  
  // 4. On form submit
  //    - Convert taxyear to last two digits
  const handleSubmit = (e) => {
    e.preventDefault();
  
    const trimmedFormData = trimFormData(formData);
    setFormData(trimmedFormData); // reflect the trimmed values in the UI

    const validationErrors = validateForm(trimmedFormData);
    if (validationErrors.length > 0) {
      alert(validationErrors.join("\n"));
      return;
    }
  
    const processedFormData = {
      ...trimmedFormData,
      payoffDate: formatMMDDYYYY(trimmedFormData.payoffDate),
      taxyear: getLastTwoDigits(trimmedFormData.taxyear),
    };
  
    console.log('Submitting Form with 2-digit Year:', processedFormData);
  
    navigate(`/customer/${customerId}/taxSearchResults`, {
      state: { formData: processedFormData },
    });
  };

  // 5. Render the form
  return (
    <form onSubmit={handleSubmit}>
      <table
        className="tableMain"
        border="0"
        width="100%"
        cellPadding="2"
        cellSpacing="2"
      >
        <tbody>
          <tr>
            <td height="10" colSpan="3">
              <span className="searchInstruction">
                For best results, please only search by the Tax ID or Name.
              </span>{' '}
              If necessary, enter additional information.
            </td>
          </tr>
          <tr>
            <td className="lbltxt">Tax ID:</td>
            <td>
              <input
                className="inputFull fieldsMed"
                type="text"
                name="taxID"
                value={formData.taxID}
                onChange={handleChange}
              />
            </td>
          </tr>
          <tr>
            <td className="lbltxt">Name:</td>
            <td>
              <input
                className="inputFull"
                type="text"
                name="firstName"
                value={formData.firstName}
                onChange={handleChange}
              />
              <div className="fieldHint">Lastname Firstname</div>
            </td>
          </tr>
          {/* 
          <tr>
            <td className="lbltxt">Last Name:</td>
            <td>
              <input
                className="inputFull"
                type="text"
                name="lastName"
                value={formData.lastName}
                onChange={handleChange}
              />
            </td>
          </tr>
          */}
          <tr>
            <td className="lbltxt">Mailing Address:</td>
            <td>
              <input
                className="inputFull"
                type="text"
                name="mailingAddress"
                value={formData.mailingAddress}
                onChange={handleChange}
              />
            </td>
          </tr>
          <tr>
            <td className="lbltxt">Legal Description:</td>
            <td>
              <input
                className="inputFull"
                type="text"
                name="legalDescription"
                value={formData.legalDescription}
                onChange={handleChange}
              />
            </td>
          </tr>
          <tr>
            <td className="lbltxt">Geo Code:</td>
            <td>
              <input
                className="inputFull"
                type="text"
                name="geoCode"
                value={formData.geoCode}
                onChange={handleChange}
              />
              <div className="fieldHint">####-##-#-##-##-####</div>
            </td>
          </tr>
          <tr>
            <td className="lbltxt">Date for Payoff:</td>
            <td>
              <DatePicker
                className="inputFull fieldsSm"
                wrapperClassName="payoffDateWrapper"
                selected={formData.payoffDate}
                onChange={(date) =>
                  setFormData((prev) => ({ ...prev, payoffDate: date ?? startOfTodayLocal() }))
                }
                minDate={startOfTodayLocal()}
                dateFormat="MM/dd/yyyy"
                onKeyDown={(e) => e.preventDefault()}
              />
            </td>
          </tr>
          <tr>
            <td className="lbltxt">Tax Year:</td>
            <td>
              <input
                className="inputFull fieldsSm"
                type="text"
                name="taxyear"
                value={formData.taxyear}
                onChange={handleChange}
              />
            </td>
          </tr>
          <tr>
            <td className="lbltxt"># of Years of History:</td>
            <td>
              <input
                className="inputFull fieldsSm"
                type="text"
                name="yearshistory"
                value={formData.yearshistory}
                onChange={handleChange}
              />
            </td>
          </tr>
          <tr>
            <td></td>
            <td>
              <input
                type="submit"
                name="propertySearch"
                value="Begin Search"
                id="propertySearch"
              />
            </td>
          </tr>
        </tbody>
      </table>

      {/* If your backend needs the customerId: */}
      <input name="customerId" type="hidden" value={formData.customerId} />
    </form>
  );
};

export default TaxSearchForm;